/**
 * Cart API Client
 *
 * ✅ SEPARATION OF CONCERNS: Pure data access layer for cart
 * ✅ DRY: Uses BaseApiClient for consistent patterns
 * ✅ MODULAR: Co-located with cart feature
 *
 * Type-safe API client for variant-first cart operations.
 * Complete cart functionality with optimistic updates support.
 *
 * @file cart.api.ts
 * @version 3.0.0 - Using BaseApiClient
 */

import { BaseApiClient } from '@/shared/lib/api/base-api-client';
import type { ApiRequestOptions } from '@/shared/lib/api/base-api-client';
import { getOrCreateSessionId } from '@/shared/utils/session';
import type {
  AddToCartRequest,
  CartResponse,
  CartTotalResponse,
  GetCartTotalParams,
} from '../types';

// Re-export types for backward compatibility
export type {
  CartLineItem,
  CartResponse,
  CartTotalResponse,
  AddToCartRequest,

} from '../types';

// ============================================================================
// CART API CLIENT
// ============================================================================

/**
 * Cart API Client
 * Extends BaseApiClient for cart-specific endpoints
 *
 * Session ID is sent as X-Session-ID header for all requests.
 * This is more secure than query parameters (prevents logging/history exposure).
 */
class CartApiClient extends BaseApiClient {
  constructor() {
    super({ basePath: 'cart' });
  }

  /**
   * Override getRequestOptions to inject session_id as header
   */
  protected override async getRequestOptions(options: ApiRequestOptions = {}): Promise<ApiRequestOptions> {
    const baseOptions = await super.getRequestOptions(options);
    const sessionId = getOrCreateSessionId();

    return {
      ...baseOptions,
      headers: {
        'X-Session-ID': sessionId,
        ...baseOptions.headers,
      },
    };
  }

  /**
   * Fetch current cart state
   *
   * @param sessionId - Session ID
   * @returns Cart response with items and totals
   */
  async getCart(sessionId: string): Promise<CartResponse> {
    return this.get<CartResponse>(sessionId);
  }

  /**
   * Get cart totals with VAT breakdown
   *
   * @param sessionId - Session ID
   * @param shippingMethodId - Optional shipping method UUID for accurate shipping cost
   * @returns Cart total response with shipping and VAT
   */
  async getCartTotal(sessionId: string, shippingMethodId?: string): Promise<CartTotalResponse> {
    const queryParams = shippingMethodId
      ? `?shipping_method_id=${encodeURIComponent(shippingMethodId)}`
      : '';
    return this.get<CartTotalResponse>(`${sessionId}/total${queryParams}`);
  }

  /**
   * Add item to cart
   *
   * Supports three modes:
   * 1. Direct variant: Provide product_variant_id
   * 2. Auto-create variant: Provide product_type_id + design_asset_id
   * 3. Catalog product: Provide (catalog_product_uid OR catalog_size_code) + design_asset_id
   *    Backend (COMPASS) will map catalog IDs to product_type_id
   *
   * @param request - Add to cart request
   * @returns Updated cart response
   */
  async addToCart(request: AddToCartRequest): Promise<CartResponse> {
    const payload = {
      session_id: getOrCreateSessionId(),
      // Mode 1 & 2: Direct variant or type + design auto-creation
      product_variant_id: request.product_variant_id,
      product_type_id: request.product_type_id,
      quantity: request.quantity ?? 1,
      generation_session_id: request.generation_session_id,
      design_asset_id: request.design_asset_id,
      country_code: request.country_code ?? 'SE',
      // Mode 3: Catalog product identifiers (COMPASS integration)
      // Backend will map these to product_type_id
      catalog_product_uid: request.catalog_product_uid,
      catalog_size_code: request.catalog_size_code,
      product_options: request.product_options,
      // SPECTRUM: Quality tier selection for figurines
      quality_tier_code: request.quality_tier_code,
    };

    return this.post<CartResponse>('add', payload);
  }

  /**
   * Update cart item quantity
   *
   * @param itemId - Cart item ID
   * @param quantity - New quantity (must be >= 1)
   * @returns Updated cart response
   */
  async updateCartItem(itemId: string, quantity: number): Promise<CartResponse> {
    return this.patch<CartResponse>(`items/${itemId}`, { quantity });
  }

  /**
   * Remove item from cart
   *
   * @param itemId - Cart item ID to remove
   * @returns Updated cart response
   */
  async removeCartItem(itemId: string): Promise<CartResponse> {
    return this.delete<CartResponse>(`items/${itemId}`);
  }

  /**
   * Clear all items from cart
   *
   * @param sessionId - Session ID
   * @returns Empty cart response
   */
  async clearCart(sessionId: string): Promise<CartResponse> {
    return this.delete<CartResponse>(sessionId);
  }

  /**
   * Update cart country and recalculate all prices
   *
   * When user selects a different country in checkout, this endpoint:
   * - Recalculates all product prices for the new country
   * - Updates VAT rates based on country tax rules
   * - Converts all prices to the country's currency
   * - Updates add-on prices
   *
   * @param sessionId - Session ID
   * @param countryCode - ISO country code (e.g., "SE", "US", "GB")
   * @returns Updated cart response with new prices/currency
   */
  async updateCartCountry(sessionId: string, countryCode: string): Promise<CartResponse> {
    return this.post<CartResponse>(`${sessionId}/country`, {
      country_code: countryCode.toUpperCase(),
    });
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

const cartApiClient = new CartApiClient();

// ============================================================================
// PUBLIC API FUNCTIONS
// ============================================================================

/**
 * Get session ID using centralized session management
 * @see shared/utils/session.ts
 */
export function getSessionId(): string {
  return getOrCreateSessionId();
}

/**
 * Fetch current cart state
 *
 * @param sessionId - Optional session ID (uses stored ID if not provided)
 * @returns Cart response with items and totals
 */
export async function getCart(sessionId?: string): Promise<CartResponse> {
  const id = sessionId || getSessionId();
  return cartApiClient.getCart(id);
}

/**
 * Get cart totals with VAT breakdown
 *
 * @param params - Optional parameters including sessionId and shippingMethodId
 * @returns Cart total response with shipping and VAT
 */
export async function getCartTotal(params?: GetCartTotalParams): Promise<CartTotalResponse> {
  const sessionId = params?.sessionId || getSessionId();
  return cartApiClient.getCartTotal(sessionId, params?.shippingMethodId);
}

/**
 * Add item to cart
 *
 * The variant-first approach: user selects a product variant and adds it to cart.
 * Design asset can be optionally linked for personalization.
 *
 * @param request - Add to cart request
 * @returns Updated cart response
 */
export async function addToCart(request: AddToCartRequest): Promise<CartResponse> {
  return cartApiClient.addToCart(request);
}

/**
 * Update cart item quantity
 *
 * @param itemId - Cart item ID
 * @param quantity - New quantity (must be >= 1)
 * @returns Updated cart response
 */
export async function updateCartItem(itemId: string, quantity: number): Promise<CartResponse> {
  return cartApiClient.updateCartItem(itemId, quantity);
}

/**
 * Remove item from cart
 *
 * @param itemId - Cart item ID to remove
 * @returns Updated cart response
 */
export async function removeCartItem(itemId: string): Promise<CartResponse> {
  return cartApiClient.removeCartItem(itemId);
}

/**
 * Clear all items from cart
 *
 * @returns Empty cart response
 */
export async function clearCart(): Promise<CartResponse> {
  return cartApiClient.clearCart(getSessionId());
}

/**
 * Update cart country and recalculate all prices
 *
 * When user selects a different country in checkout, this:
 * - Recalculates all product prices for the new country
 * - Updates VAT rates based on country tax rules
 * - Converts all prices to the country's currency
 * - Updates add-on prices
 *
 * @param countryCode - ISO country code (e.g., "SE", "US", "GB")
 * @returns Updated cart response with new prices/currency
 */
export async function updateCartCountry(countryCode: string): Promise<CartResponse> {
  return cartApiClient.updateCartCountry(getSessionId(), countryCode);
}
